<template>
  <div class="pos-page py-3">
    <div class="pos-layout">
      <!-- Productos -->
      <section class="pos-products card border-0 shadow-sm">
        <div class="card-body">
          <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
            <h6 class="text-uppercase text-muted mb-0">Productos</h6>
            <div class="d-flex align-items-center gap-2 pos-products-toolbar">
              <input
                v-model="search"
                type="text"
                class="form-control pos-search"
                placeholder="Buscar producto..."
                @input="onSearch"
              />
              <select
                v-model="sourceFilter"
                class="form-select pos-source-select"
                @change="onSourceFilterChange"
              >
                <option value="siigo">Siigo</option>
                <option value="local">Local</option>
              </select>
              <select
                v-model.number="priceLevel"
                class="form-select pos-price-select"
                @change="onPriceLevelChange"
              >
                <option v-for="n in 5" :key="n" :value="n">Precio {{ n }}</option>
              </select>
            </div>
          </div>

          <div v-if="loadingProducts" class="text-center py-5">
            <i class="fas fa-spinner fa-spin fa-2x text-success"></i>
          </div>

          <div v-else class="pos-grid">
            <button
              v-for="product in products"
              :key="product.id"
              type="button"
              class="pos-product-card"
              :class="'pos-product-card--' + product.stock_level"
              :disabled="!canAddProduct(product)"
              @click="addToCart(product)"
            >
              <span
                class="pos-source-badge"
                :class="productSourceClass(product)"
                :title="productSourceTitle(product)"
              >
                {{ productSourceLabel(product) }}
              </span>
              <span class="pos-stock-badge">{{ stockLabel(product) }}</span>
              <div class="pos-product-image">
                <img
                  v-if="productImageUrl(product)"
                  :src="productImageUrl(product)"
                  :alt="product.name"
                  class="pos-product-img"
                  loading="lazy"
                  @error="onProductImageError($event, product)"
                />
                <i v-else class="fas fa-box-open"></i>
              </div>
              <p class="pos-product-name">{{ product.name }}</p>
              <p class="pos-product-ref">Ref: {{ product.ref || '—' }}</p>
              <span class="pos-product-price">{{ formatCurrency(getProductPrice(product, priceLevel)) }}</span>
            </button>
          </div>

          <div
            v-if="!loadingProducts && products.length === 0"
            class="text-center text-muted py-4"
          >
            No hay productos disponibles
          </div>

          <div class="d-flex align-items-center justify-content-between mt-3 pt-2 border-top">
            <span class="text-sm text-muted">
              Mostrando {{ pagination.from || 0 }}-{{ pagination.to || 0 }} de {{ pagination.total || 0 }}
            </span>
            <div class="pos-pagination">
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary pos-pagination-btn"
                :disabled="pagination.currentPage <= 1"
                @click="changePage(pagination.currentPage - 1)"
              >
                &lt;
              </button>
              <span class="pos-pagination-label">
                {{ pagination.currentPage }} / {{ pagination.lastPage || 1 }}
              </span>
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary pos-pagination-btn"
                :disabled="pagination.currentPage >= pagination.lastPage"
                @click="changePage(pagination.currentPage + 1)"
              >
                &gt;
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- Carrito -->
      <aside class="pos-cart card border-0 shadow-sm">
        <div class="card-body d-flex flex-column h-100">
          <h6 class="text-uppercase mb-3">Carrito de venta</h6>

          <label class="form-control-label text-sm">Cliente</label>
          <div class="d-flex gap-2 mb-3 pos-client-row">
            <div class="flex-grow-1 pos-client-select-wrap">
              <select ref="clientSelect" class="form-select form-select-sm select2" style="width: 100%"></select>
            </div>
            <button
              type="button"
              class="btn btn-success btn-sm px-3"
              title="Nuevo cliente"
              @click="openClientModal"
            >
              <i class="fas fa-plus"></i>
            </button>
          </div>

          <p class="text-uppercase text-sm text-muted mb-2">Productos</p>
          <div class="pos-cart-items flex-grow-1">
            <p v-if="cart.length === 0" class="text-muted text-sm">El carrito está vacío</p>
            <div
              v-for="(item, index) in cart"
              :key="item.inventory_id"
              class="pos-cart-card"
            >
              <div class="pos-cart-card-header">
                <div class="pos-cart-card-title">
                  <span
                    class="pos-source-badge pos-source-badge--cart"
                    :class="item.source === 'siigo' ? 'pos-source-badge--siigo' : 'pos-source-badge--local'"
                  >
                    {{ item.source === 'siigo' ? 'Siigo' : 'Local' }}
                  </span>
                  <span class="pos-cart-card-name">{{ item.name }}</span>
                </div>
                <button
                  type="button"
                  class="pos-cart-card-remove"
                  aria-label="Quitar producto"
                  @click="removeFromCart(index)"
                >
                  <i class="fas fa-times"></i>
                </button>
              </div>

              <div class="pos-cart-card-qty">
                <button type="button" class="pos-qty-btn" @click="changeQty(index, -1)">−</button>
                <span class="pos-qty-value">{{ item.quantity }}</span>
                <button type="button" class="pos-qty-btn" @click="changeQty(index, 1)">+</button>
              </div>

              <div class="pos-cart-card-prices">
                <div>
                  <span class="pos-cart-price-label">Precio unitario</span>
                  <span class="pos-cart-price-value">{{ formatCurrency(item.unit_price) }}</span>
                </div>
                <div class="text-end">
                  <span class="pos-cart-price-label">Precio total</span>
                  <span class="pos-cart-price-total">{{ formatCurrency(lineTotal(item)) }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="pos-totals mt-3">
            <div class="d-flex justify-content-between text-sm">
              <span>Subtotal</span>
              <span>{{ formatCurrency(cartSubtotal) }}</span>
            </div>
            <div class="d-flex justify-content-between mt-2">
              <span class="font-weight-bold">TOTAL</span>
              <span class="pos-total-amount">{{ formatCurrency(cartTotal) }}</span>
            </div>
          </div>

          <button
            type="button"
            class="btn btn-success w-100 mt-3"
            :disabled="saving || cart.length === 0"
            @click="saveSale"
          >
            <i class="fas fa-save me-1"></i>
            {{ saving ? 'Guardando...' : 'Guardar' }}
          </button>
        </div>
      </aside>
    </div>

    <!-- Modal cliente -->
    <div class="modal fade" id="posClientModal" tabindex="-1" ref="clientModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Nuevo cliente</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <label class="form-control-label">Nombre <span class="text-danger">*</span></label>
            <input v-model="newClient.name" type="text" class="form-control mb-3" />
            <label class="form-control-label">Documento</label>
            <input v-model="newClient.document" type="text" class="form-control mb-3" />
            <label class="form-control-label">Correo</label>
            <input
              v-model="newClient.email"
              type="email"
              class="form-control mb-1"
              placeholder="Opcional"
            />
            <p class="text-muted text-xs mb-3">
              Si no indica correo, se usará cliente+hash@mistock.co
            </p>
            <label class="form-control-label">Clave</label>
            <input
              v-model="newClient.password"
              type="password"
              class="form-control mb-1"
              placeholder="Opcional"
              autocomplete="new-password"
            />
            <p class="text-muted text-xs mb-0">
              Si no indica clave, se generará una contraseña aleatoria segura.
            </p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
            <button type="button" class="btn btn-success" @click="saveClient">Guardar</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import posService from "@/services/pos.service";
import { buildInventoryImageUrl, inventoryTicketName } from "@/utils/inventoryImage";
import Swal from "sweetalert2";
import { Modal } from "bootstrap";
import $ from "jquery";
import "select2/dist/css/select2.css";
import "select2";
import {
  savePosTicketData,
  openPosTicketWindow,
  buildInvoiceNumber,
  formatTicketDate,
} from "@/utils/posTicketPrint";

export default {
  name: "Pos",
  data() {
    return {
      search: "",
      searchTimer: null,
      products: [],
      loadingProducts: false,
      pagination: {
        currentPage: 1,
        lastPage: 1,
        total: 0,
        from: 0,
        to: 0,
      },
      clients: [],
      selectedClientId: null,
      cart: [],
      saving: false,
      newClient: { name: "", document: "", email: "", password: "" },
      clientModal: null,
      priceLevel: 1,
      sourceFilter: "siigo",
      companySettings: {
        company_name: "",
        description: "",
        phone: "",
        address: "",
        email: "",
        logo_path: "",
        logo_path_dark: "",
      },
    };
  },
  computed: {
    cartSubtotal() {
      return this.cart.reduce((sum, item) => sum + item.quantity * item.unit_price, 0);
    },
    cartTotal() {
      return this.cart.reduce((sum, item) => sum + this.lineTotal(item), 0);
    },
    cartProductSource() {
      if (!this.cart.length) {
        return null;
      }
      return this.isSiigoProduct(this.cart[0]) ? "siigo" : "local";
    },
  },
  mounted() {
    this.init();
    this.clientModal = new Modal(this.$refs.clientModal);
  },
  beforeUnmount() {
    this.destroyClientSelect2();
  },
  methods: {
    async init() {
      await Promise.all([this.loadClients(), this.loadProducts(), this.loadCompanySettings()]);
    },
    async loadCompanySettings() {
      try {
        const response = await this.$store.dispatch("settings/get");
        const data = response?.data || response || {};
        this.companySettings = {
          company_name: data.company_name || "",
          description: data.description || "",
          phone: data.phone || "",
          address: data.address || "",
          email: data.email || "",
          logo_path: data.logo_path || "",
          logo_path_dark: data.logo_path_dark || "",
        };
      } catch (e) {
        console.error("No se pudo cargar configuración de empresa:", e);
      }
    },
    buildTicketPayload(remissionId, client) {
      const items = this.cart.map((item) => ({
        name: inventoryTicketName(item),
        ref: item.ref,
        quantity: item.quantity,
        unitPrice: item.unit_price,
        total: this.lineTotal(item),
      }));
      const articlesCount = items.reduce((sum, i) => sum + Number(i.quantity || 0), 0);
      const total = this.cartTotal;
      let seller = "LOCAL";
      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        seller = user?.name || seller;
      } catch {
        /* ignore */
      }

      return {
        remissionId,
        invoiceNo: buildInvoiceNumber(remissionId),
        dateFormatted: formatTicketDate(new Date()),
        client: {
          id: client.id,
          name: client.name,
          document_type: client.document_type || "CC",
          document: client.document || String(client.id),
        },
        items,
        subtotal: this.cartSubtotal,
        total,
        received: total,
        change: 0,
        paymentMethod: "CONTADO",
        articlesCount,
        itemsCount: items.length,
        seller: String(seller).toUpperCase(),
        company: { ...this.companySettings },
      };
    },
    async loadProducts(page = 1) {
      this.loadingProducts = true;
      try {
        const params = {
          page,
          per_page: 12,
          source: this.sourceFilter === "local" ? "local" : "siigo",
        };
        if (this.search) {
          params.search = this.search;
        }
        const res = await posService.getProducts(params);
        const data = res.data;
        const rows = data.data || [];
        this.products = Array.isArray(rows) ? rows.map((item) => this.mapProduct(item)) : [];
        this.pagination = {
          currentPage: data.current_page || 1,
          lastPage: data.last_page || 1,
          total: data.total || this.products.length,
          from: data.from || (this.products.length ? 1 : 0),
          to: data.to || this.products.length,
        };
      } catch (e) {
        console.error(e);
        this.products = [];
      } finally {
        this.loadingProducts = false;
      }
    },
    mapProduct(item) {
      const source = item.source === "siigo" || (item.siigo && String(item.siigo).trim() !== "")
        ? "siigo"
        : "local";
      return {
        ...item,
        source,
        siigo: item.siigo || null,
        precio1: parseFloat(item.precio1 || 0),
        precio2: parseFloat(item.precio2 || 0),
        precio3: parseFloat(item.precio3 || 0),
        precio4: parseFloat(item.precio4 || 0),
        precio5: parseFloat(item.precio5 || 0),
        price: parseFloat(item.price || 0),
        iva: parseFloat(item.iva || 0),
        descuento: parseFloat(item.descuento || 0),
        imageLoadFailed: false,
        image_url:
          buildInventoryImageUrl(item.image_path) ||
          buildInventoryImageUrl(item.image_url),
      };
    },
    productImageUrl(product) {
      if (!product || product.imageLoadFailed) return null;
      if (!product.image_path && !product.image_url) return null;
      return (
        buildInventoryImageUrl(product.image_path) ||
        buildInventoryImageUrl(product.image_url)
      );
    },
    onProductImageError(_event, product) {
      if (product) {
        product.imageLoadFailed = true;
      }
    },
    async loadClients(selectId = null) {
      try {
        const res = await posService.getClients();
        this.clients = res.data || [];
        if (selectId) {
          this.selectedClientId = selectId;
        } else if (this.clients.length && !this.selectedClientId) {
          this.selectedClientId = this.clients[0].id;
        }
        await this.$nextTick();
        this.initClientSelect2(selectId);
      } catch (e) {
        console.error("Error al cargar clientes:", e);
        this.clients = [];
      }
    },
    destroyClientSelect2() {
      const select = this.$refs.clientSelect;
      if (!select) return;
      const $select = $(select);
      if ($select.hasClass("select2-hidden-accessible")) {
        $select.off("change");
        $select.select2("destroy");
      }
    },
    initClientSelect2(selectId = null) {
      const select = this.$refs.clientSelect;
      if (!select) return;

      const $select = $(select);
      this.destroyClientSelect2();

      $select.empty();
      $select.append('<option value="">Seleccionar cliente</option>');

      this.clients.forEach((client) => {
        const extra = client.email || client.document || client.phone || "";
        const label = extra
          ? `${client.id} - ${client.name} (${extra})`
          : `${client.id} - ${client.name}`;
        $select.append(new Option(label, client.id, false, false));
      });

      $select.select2({
        placeholder: "Seleccione un cliente",
        allowClear: false,
        width: "100%",
        dropdownParent: $(this.$el).find(".pos-client-select-wrap"),
      });

      $select.on("change", () => {
        const id = $select.val();
        this.selectedClientId = id ? Number(id) : null;
      });

      const idToSet = selectId ?? this.selectedClientId;
      if (idToSet) {
        $select.val(String(idToSet)).trigger("change");
      }
    },
    onSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.loadProducts(1), 350);
    },
    changePage(page) {
      if (page < 1 || page > this.pagination.lastPage) return;
      this.loadProducts(page);
    },
    getProductPrice(product, level = this.priceLevel) {
      const lvl = Number(level) || 1;
      const raw = product[`precio${lvl}`];
      if (raw === null || raw === undefined || raw === "") {
        return 0;
      }
      const selected = parseFloat(raw);
      return Number.isNaN(selected) ? 0 : selected;
    },
    stockLabel(product) {
      if (!product.manage_stock) return "∞";
      return Math.floor(product.quantity || 0);
    },
    productSource(product) {
      if (product?.source === "siigo" || product?.source === "local") {
        return product.source;
      }
      return this.isSiigoProduct(product) ? "siigo" : "local";
    },
    isSiigoProduct(product) {
      return this.productSource(product) === "siigo";
    },
    productSourceLabel(product) {
      return this.productSource(product) === "siigo" ? "Siigo" : "Local";
    },
    productSourceClass(product) {
      return this.productSource(product) === "siigo"
        ? "pos-source-badge--siigo"
        : "pos-source-badge--local";
    },
    productSourceTitle(product) {
      if (this.productSource(product) === "siigo" && product?.siigo) {
        return String(product.siigo);
      }
      return "Producto local";
    },
    productMatchesCartSource(product) {
      if (!this.cart.length) {
        return true;
      }
      const cartIsSiigo = this.cartProductSource === "siigo";
      return cartIsSiigo === this.isSiigoProduct(product);
    },
    canAddProduct(product) {
      if (!this.productMatchesCartSource(product)) {
        return false;
      }
      if (!product.manage_stock) return true;
      return (product.quantity || 0) > 0;
    },
    addToCart(product) {
      if (!this.productMatchesCartSource(product)) {
        const cartLabel = this.cartProductSource === "siigo" ? "Siigo" : "locales";
        const productLabel = this.isSiigoProduct(product) ? "Siigo" : "local";
        Swal.fire(
          "Tipo de producto",
          `El carrito solo admite productos ${cartLabel}. Este producto es ${productLabel}.`,
          "warning"
        );
        return;
      }
      if (!this.canAddProduct(product)) {
        Swal.fire("Sin stock", "No hay unidades disponibles de este producto.", "warning");
        return;
      }
      const existing = this.cart.find((c) => c.inventory_id === product.id);
      if (existing) {
        if (product.manage_stock && existing.quantity >= product.quantity) {
          Swal.fire("Stock máximo", "No puede agregar más unidades.", "warning");
          return;
        }
        existing.quantity += 1;
        existing.price_level = this.priceLevel;
        this.updateCartPrice(existing);
        return;
      }
      this.cart.push({
        inventory_id: product.id,
        name: product.name,
        invoice_name: product.invoice_name || null,
        ref: product.ref,
        siigo: product.siigo || null,
        source: this.productSource(product),
        quantity: 1,
        unit_price: this.getProductPrice(product),
        price_level: this.priceLevel,
        iva: parseFloat(product.iva || 0),
        descuento: parseFloat(product.descuento || 0),
        manage_stock: product.manage_stock,
        max_stock: product.quantity,
        precio1: product.precio1,
        precio2: product.precio2,
        precio3: product.precio3,
        precio4: product.precio4,
        precio5: product.precio5,
        price: product.price,
      });
      if (this.cart.length === 1 && this.cartProductSource && this.sourceFilter !== this.cartProductSource) {
        this.sourceFilter = this.cartProductSource;
        this.loadProducts(1);
      }
    },
    onSourceFilterChange() {
      if (
        this.cart.length &&
        this.cartProductSource &&
        this.sourceFilter !== this.cartProductSource
      ) {
        Swal.fire(
          "Tipo de producto",
          `Vacíe el carrito o filtre por productos ${
            this.cartProductSource === "siigo" ? "Siigo" : "locales"
          } para seguir agregando.`,
          "warning"
        );
        this.sourceFilter = this.cartProductSource;
        return;
      }
      this.loadProducts(1);
    },
    onPriceLevelChange() {
      this.priceLevel = Number(this.priceLevel) || 1;
      this.cart.forEach((item) => {
        item.price_level = this.priceLevel;
        this.updateCartPrice(item);
      });
    },
    updateCartPrice(item) {
      item.unit_price = this.getProductPrice(item, item.price_level);
    },
    changeQty(index, delta) {
      const item = this.cart[index];
      const next = item.quantity + delta;
      if (next < 1) {
        this.removeFromCart(index);
        return;
      }
      if (item.manage_stock && next > item.max_stock) {
        Swal.fire("Stock máximo", "No hay más unidades disponibles.", "warning");
        return;
      }
      item.quantity = next;
    },
    removeFromCart(index) {
      this.cart.splice(index, 1);
    },
    lineTotal(item) {
      const sub = item.quantity * item.unit_price;
      const afterDisc = sub * (1 - (item.descuento || 0) / 100);
      return afterDisc * (1 + (item.iva || 0) / 100);
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        minimumFractionDigits: 2,
      }).format(value || 0);
    },
    openClientModal() {
      this.newClient = { name: "", document: "", email: "", password: "" };
      this.clientModal.show();
    },
    async saveClient() {
      if (!this.newClient.name.trim()) {
        Swal.fire("Nombre requerido", "Indique el nombre del cliente.", "warning");
        return;
      }
      try {
        const payload = {
          name: this.newClient.name.trim(),
          document: this.newClient.document?.trim() || null,
          email: this.newClient.email?.trim() || null,
          password: this.newClient.password || null,
        };
        const res = await posService.createClient(payload);
        await this.loadClients(res.data.id);
        this.clientModal.hide();
        Swal.fire(
          "Cliente creado",
          res.data.email ? `Correo: ${res.data.email}` : "",
          "success"
        );
      } catch (e) {
        const msg =
          e?.response?.data?.message ||
          (e?.response?.data?.errors
            ? Object.values(e.response.data.errors).flat().join(" ")
            : null) ||
          "No se pudo guardar el cliente.";
        Swal.fire("Error", msg, "error");
      }
    },
    buildRemissionForm(client) {
      const doc = client?.document ? String(client.document) : String(client?.id || 0);
      return {
        id: 0,
        user_id: client?.id || null,
        client_id: client?.id || null,
        name: client?.name || "Cliente general",
        document_type: client?.document_type || "CC",
        document: doc,
        address: client?.address || "N/A",
        phone: client?.phone || "0000000000",
        email: client?.email || `cliente${client?.id || 0}@mistock.co`,
        observation: "Venta POS",
        status: 0,
        type: "pos",
      };
    },
    async saveSale() {
      if (!this.selectedClientId) {
        Swal.fire("Cliente requerido", "Seleccione un cliente para la venta.", "warning");
        return;
      }
      this.saving = true;
      try {
        const client = this.clients.find((c) => c.id == this.selectedClientId);
        if (!client) {
          throw new Error("Cliente no encontrado.");
        }

        const remissionResponse = await this.$store.dispatch(
          "remissions/save",
          this.buildRemissionForm(client)
        );

        if (!remissionResponse || remissionResponse.status < 200 || remissionResponse.status >= 300) {
          throw new Error(remissionResponse?.data?.message || "No se pudo guardar la venta.");
        }

        const remissionId = remissionResponse.data?.id;
        if (!remissionId) {
          throw new Error("No se obtuvo el identificador de la venta.");
        }

        for (const item of this.cart) {
          const detailResponse = await this.$store.dispatch("remissionsDetails/save", {
            id: 0,
            remission_id: remissionId,
            inventory_id: item.inventory_id,
            quantity: item.quantity,
            price: item.unit_price,
          });

          if (!detailResponse || detailResponse.status < 200 || detailResponse.status >= 300) {
            throw new Error(
              detailResponse?.data?.message ||
                `No se pudo guardar el detalle de ${item.name}.`
            );
          }
        }

        const formalizeResponse = await this.$store.dispatch("remissions/formalize", remissionId);
        if (!formalizeResponse || formalizeResponse.status < 200 || formalizeResponse.status >= 300) {
          throw new Error(
            formalizeResponse?.data?.message || "La venta se guardó pero no se pudo formalizar."
          );
        }

        await this.loadCompanySettings();
        const ticketPayload = this.buildTicketPayload(remissionId, client);
        savePosTicketData(ticketPayload);

        this.cart = [];
        await this.loadProducts(this.pagination.currentPage);
        await Swal.fire(
          "Venta registrada",
          `Venta #${remissionId} guardada en Ventas correctamente.`,
          "success"
        );
        openPosTicketWindow(this.$router, remissionId);
      } catch (e) {
        const msg =
          e?.response?.data?.message ||
          e?.data?.message ||
          e?.message ||
          "No se pudo guardar la venta.";
        Swal.fire("Error", msg, "error");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.pos-page {
  min-height: calc(100vh - 120px);
}

.pos-layout {
  display: grid;
  grid-template-columns: 1fr 360px;
  gap: 1rem;
  align-items: start;
}

@media (max-width: 992px) {
  .pos-layout {
    grid-template-columns: 1fr;
  }
}

.pos-products-toolbar {
  flex: 1;
  justify-content: flex-end;
  min-width: 0;
}

.pos-search {
  max-width: 280px;
  min-width: 160px;
  flex: 1;
}

.pos-source-select,
.pos-price-select {
  width: auto;
  min-width: 7rem;
  flex-shrink: 0;
}

.pos-pagination {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  height: 31px;
}

.pos-pagination-btn {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  box-sizing: border-box;
  width: 31px !important;
  height: 31px !important;
  min-height: 31px !important;
  padding: 0 !important;
  margin: 0 !important;
  line-height: 1 !important;
  flex-shrink: 0;
}

.pos-pagination-label {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  box-sizing: border-box;
  height: 31px !important;
  min-height: 31px !important;
  padding: 0 0.35rem !important;
  margin: 0 !important;
  line-height: 1 !important;
  font-size: 0.875rem;
  color: #67748e;
  flex-shrink: 0;
}

.pos-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.75rem;
}

@media (max-width: 1400px) {
  .pos-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .pos-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.pos-product-card {
  position: relative;
  border: 2px solid #82d616;
  border-radius: 12px;
  background: #fff;
  padding: 0.75rem 0.5rem 2.5rem;
  text-align: center;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.pos-product-card:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.pos-product-card:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.pos-product-card--medium {
  border-color: #f5a623;
}

.pos-product-card--low {
  border-color: #ea0606;
}

.pos-source-badge {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 1;
  font-size: 0.65rem;
  font-weight: 700;
  line-height: 1.2;
  padding: 3px 7px;
  border-radius: 6px;
  color: #fff;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.pos-source-badge--siigo {
  background: #17c1e8;
}

.pos-source-badge--local {
  background: #8392ab;
}

.pos-source-badge--cart {
  position: static;
  display: inline-block;
  margin-right: 0.35rem;
  vertical-align: middle;
  font-size: 0.6rem;
  padding: 2px 6px;
}

.pos-cart-card-title {
  display: flex;
  align-items: flex-start;
  gap: 0;
  min-width: 0;
  flex: 1;
}

.pos-cart-card-title .pos-cart-card-name {
  flex: 1;
  min-width: 0;
}

.pos-stock-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  background: #82d616;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  min-width: 26px;
  height: 26px;
  line-height: 26px;
  border-radius: 50%;
  padding: 0 4px;
}

.pos-product-card--medium .pos-stock-badge {
  background: #f5a623;
}

.pos-product-card--low .pos-stock-badge {
  background: #ea0606;
}

.pos-product-image {
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #adb5bd;
  font-size: 2rem;
  overflow: hidden;
}

.pos-product-img {
  max-width: 100%;
  max-height: 72px;
  width: auto;
  height: auto;
  object-fit: contain;
}

.pos-product-name {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  margin: 0.25rem 0;
  line-height: 1.2;
  min-height: 2.4em;
}

.pos-product-ref {
  font-size: 0.65rem;
  color: #8392ab;
  margin-bottom: 0.5rem;
}

.pos-product-price {
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  background: #82d616;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.2rem 0.75rem;
  border-radius: 999px;
  white-space: nowrap;
}

.pos-client-row {
  align-items: stretch;
}

.pos-client-select-wrap {
  min-width: 0;
}

.pos-client-select-wrap :deep(.select2-container) {
  width: 100% !important;
}

.pos-client-select-wrap :deep(.select2-selection--single) {
  min-height: 31px;
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  font-size: 0.875rem;
}

.pos-client-select-wrap :deep(.select2-selection__rendered) {
  line-height: 29px;
  padding-left: 0.5rem;
}

.pos-cart {
  min-height: 520px;
  position: sticky;
  top: 1rem;
}

.pos-cart-items {
  overflow-y: auto;
  max-height: 320px;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.pos-cart-card {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 10px;
  padding: 0.75rem 0.85rem;
}

.pos-cart-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.65rem;
}

.pos-cart-card-name {
  font-size: 0.8rem;
  font-weight: 700;
  color: #344767;
  text-transform: uppercase;
  line-height: 1.25;
}

.pos-cart-card-remove {
  border: none;
  background: transparent;
  color: #ea0606;
  padding: 0;
  line-height: 1;
  font-size: 0.85rem;
  cursor: pointer;
}

.pos-cart-card-qty {
  display: inline-flex;
  align-items: stretch;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  margin-bottom: 0.75rem;
}

.pos-qty-btn {
  border: none;
  background: #fff;
  color: #344767;
  width: 2rem;
  font-size: 1rem;
  line-height: 1;
  padding: 0.35rem 0;
  cursor: pointer;
}

.pos-qty-btn:not(:last-child) {
  border-right: 1px solid #dee2e6;
}

.pos-qty-value {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #344767;
  border-right: 1px solid #dee2e6;
  background: #fff;
}

.pos-cart-card-prices {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 1rem;
}

.pos-cart-price-label {
  display: block;
  font-size: 0.7rem;
  color: #8392ab;
  margin-bottom: 0.15rem;
}

.pos-cart-price-value {
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  color: #344767;
}

.pos-cart-price-total {
  display: block;
  font-size: 0.8rem;
  font-weight: 700;
  color: #82d616;
}

.pos-totals {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 0.75rem 1rem;
}

.pos-total-amount {
  color: #82d616;
  font-size: 1.25rem;
  font-weight: 700;
}

.btn-xs {
  padding: 0.1rem 0.45rem;
  font-size: 0.75rem;
}
</style>
